import { CostProjectionDto, ParkerCostDto } from "./CostProjectionSection";
import { SubscriptionParker } from "./Data/ApiTypes";
import { toIsoLocalDate } from "./Data/Date";
import { ServerRequestsCostProjectionDto } from "./shared/api/axios-client";

/** Fingerprint of group contract rules only. Empty new groups do not change this. */
export function costProjectionContractRulesKey(
  groups: Array<{
    id?: number | null;
    contractRules?: Array<{
      min?: number | null;
      max?: number | null;
      price?: number | null;
      type?: number | null;
    }> | null;
  }>,
): string {
  return groups
    .filter((g) => (g.contractRules?.length ?? 0) > 0)
    .map((g) => {
      const rules = (g.contractRules ?? [])
        .map(
          (r) =>
            `${r.min ?? ""}-${r.max ?? ""}-${r.price ?? ""}-${r.type ?? ""}`,
        )
        .join(",");
      return `${g.id ?? ""}:${rules}`;
    })
    .sort()
    .join("|");
}

/** Structural fingerprint for cost-projection refresh. Ignores name/email/phone. */
export function costProjectionStructureKey(
  parkers: Array<{
    parker?: { parkerId?: number };
    parkerId?: number;
    subscriptionGroupId?: number | null;
    groupId?: number | null;
    quantity?: number;
    startDate?: unknown;
    removedWhen?: unknown;
    status?: unknown;
    isDraft?: boolean;
    draftId?: string;
  }>,
  extra?: string | number,
): string {
  const parkersKey = parkers
    .map((p) =>
      [
        p.parker?.parkerId ?? p.parkerId ?? "",
        p.subscriptionGroupId ?? p.groupId ?? "",
        p.quantity ?? "",
        String(p.startDate ?? ""),
        String(p.removedWhen ?? ""),
        p.status ?? "",
        p.isDraft ? "1" : "0",
        p.draftId ?? "",
      ].join(":"),
    )
    .join("|");
  return extra != null ? `${parkersKey}#${extra}` : parkersKey;
}

export function mapCostProjection(
  dto: ServerRequestsCostProjectionDto,
  parkers: SubscriptionParker[],
): CostProjectionDto | null {
  if (!dto) return null;

  const groups = (dto.groups ?? []).map((g) => {
    const groupParkers =
      g.groupId != null
        ? parkers.filter((p) => p.subscriptionGroupId === g.groupId)
        : [];

    // Map parkerCosts from API (cast as any since API types may not be updated yet)
    const rawParkerCosts = (g as any).parkerCosts ?? [];
    const parkerCosts: ParkerCostDto[] = rawParkerCosts.map((pc: any) => ({
      subscriptionParkerId: pc.subscriptionParkerId ?? 0,
      parkerName: pc.parkerName ?? "",
      startDate: pc.startDate ?? null,
      basePrice: pc.basePrice ?? 0,
      adjustment: pc.adjustment ?? 0,
      finalPrice: pc.finalPrice ?? 0,
      wasProrated: pc.wasProrated ?? false,
    }));

    return {
      groupId: g.groupId ?? null,
      groupName: g.groupName ?? "Unallocated",
      parkerCount: g.parkerCount ?? 0,
      subtotal: g.subtotal?.amount ?? 0,

      parkers: groupParkers.map((p) => ({
        name: p.parker.name,
        startDate: p.startDate
      })),

      parkerCosts,

      appliedTiers: [],

      prorationLabel:
        (g.parkerCount ?? 0) > 0
          ? "Prorated charges for mid-period additions"
          : undefined,

      lineItems:
        g.lineItems?.map((li) => ({
          description: li.description ?? "",
          quantity: li.quantity ?? undefined,
          unitPrice: li.unitPrice?.amount ?? undefined,
          total: li.total?.amount ?? 0,
        })) ?? [],
    };
  });

  const basePrice =
    dto.groups
      ?.flatMap((g) => g.lineItems ?? [])
      .find((li) => (li.unitPrice?.amount ?? 0) > 0)
      ?.unitPrice?.amount ?? 0;

  return {
    total: dto.total!,
    basePrice,
    hasPreviousPeriodActivity: dto.hasPreviousPeriodActivity!,
    periodStartDate: toIsoLocalDate(dto.periodStartDate),
    groups,
  };
}